import { VariableType } from '@/models/etc/types';

function chainEquals(a: any[], b: any[]): boolean {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export function hasSelectedChildren(node: any, nestedKeyName: string): boolean {
  const children = node?.[nestedKeyName];
  if (!Array.isArray(children) || !children.length) return false;

  for (const child of children) {
    if (child?.Selectable && child?.Selected) return true;
    if (hasSelectedChildren(child, nestedKeyName)) return true;
  }

  return false;
}

export function modelElement(node: any, opts: {
  isColumn?: boolean;
  elementType: VariableType;
  takeIdsOnly?: boolean;
  entityInsideName?: string;
}) {
  if (opts.isColumn) return node?.IdChain;
  if (opts.elementType === VariableType.Entity && opts.takeIdsOnly) return node?.Id;
  if (opts.elementType === VariableType.EntityInside) {
    return node?.[opts.entityInsideName || '']?.Id ?? node;
  }
  return node;
}

export function isNodeSelected(node: any, model: any[], opts: {
  returnType: string;
  isColumn?: boolean;
  elementType: VariableType;
  takeIdsOnly?: boolean;
  entityInsideName?: string;
  chain: any[];
}) {
  if (opts.returnType === 'none') return !!node?.Selected;

  if (!Array.isArray(model)) return false;
  const current = opts.returnType === 'chain'
    ? opts.chain
    : modelElement(node, opts);

  return model.findIndex((el: any) => {
    if (opts.returnType === 'chain') return chainEquals(el, current);
    if (opts.isColumn) return chainEquals(el?.IdChain || [], current || []);
    return el === current;
  }) !== -1;
}

export function toggleSelection(node: any, checked: boolean, model: any[], opts: {
  returnType: string;
  allowMultiple?: boolean;
  chain: any[];
  nestedKeyName: string;
  isColumn?: boolean;
  elementType: VariableType;
  takeIdsOnly?: boolean;
  entityInsideName?: string;
}): any[] {
  if (opts.returnType === 'none') {
    if (!node) return model;
    // Preserve original behavior: parent with selected descendants cannot be unchecked by parent click.
    if (!node.Selected || !hasSelectedChildren(node, opts.nestedKeyName)) {
      node.Selected = checked;
      if (node.Positive !== undefined) node.Positive = checked;
    }
    return model;
  }

  const current = opts.returnType === 'chain'
    ? opts.chain
    : modelElement(node, opts);

  const index = Array.isArray(model)
    ? model.findIndex((el: any) => {
      if (opts.returnType === 'chain') return chainEquals(el, current);
      if (opts.isColumn) return chainEquals(el?.IdChain || [], current || []);
      return el === current;
    })
    : -1;

  if (checked) {
    if (index !== -1) return model;
    if (opts.allowMultiple) return [...model, current];
    return [current];
  }

  if (index === -1) return model;
  const next = [...model];
  next.splice(index, 1);
  return next;
}
